import React, { useMemo, useState } from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import {
  detectProjectSourceType,
  getProjectSourceDisplayName as resolveProjectSourceDisplayName,
  parseProjectSourceUrl,
} from "../../shared/projectSourceDisplay.js";

const TYPE_META = {
  slack: { label: "Slack channel", icon: "#", color: T.purple },
  google: { label: "Google Doc", icon: "G", color: T.blue },
  atlassian: { label: "Atlassian link", icon: "J", color: T.decisionFg },
  asana: { label: "Asana link", icon: "A", color: T.pendingAmber },
  other: { label: "Other link", icon: "↗", color: T.mid },
};

export function detectSourceLink(value) {
  const parsed = parseProjectSourceUrl(value);
  if (!parsed) return null;
  const sourceType = detectProjectSourceType(parsed);
  const source = { rawUrl: value.trim(), sourceType };
  const displayName = getProjectSourceDisplayName(source);
  return { ...source, label: displayName, displayName };
}

export function getProjectSourceDisplayName(source = {}) {
  return resolveProjectSourceDisplayName({
    sourceType: source.sourceType,
    rawUrl: source.rawUrl ?? source.url,
    label: source.friendlyName ?? source.sourceLabel ?? source.displayName ?? source.label ?? source.name,
  }) || TYPE_META[source.sourceType]?.label || "Other link";
}

export function ProjectSourceChips({
  sources = [],
  canEdit = false,
  onRemove,
  compact = false,
  allowPortfolioRemoval = false,
  showScope = false,
}) {
  if (!sources.length) {
    return <div style={{ color: T.light, fontSize: T.fsXs, fontStyle: "italic" }}>No sources linked.</div>;
  }
  return (
    <div data-testid="project-source-chips" style={{ display: "flex", flexWrap: "wrap", gap: compact ? 4 : 7 }}>
      {sources.map((source) => {
        const meta = TYPE_META[source.sourceType] || TYPE_META.other;
        const displayName = getProjectSourceDisplayName(source);
        const removable = canEdit
          && typeof onRemove === "function"
          && (source.scope !== "portfolio" || allowPortfolioRemoval);
        return (
          <span
            key={source.id}
            data-testid={`project-typed-source-${source.id}`}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              border: `1px solid ${T.border}`, borderRadius: compact ? 6 : 8,
              padding: compact ? "3px 6px" : "5px 8px", background: T.white,
              fontSize: compact ? T.fsXxs : T.fsXs,
            }}
          >
            <span aria-label={meta.label} title={meta.label} style={{ width: 16, height: 16, borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", color: T.white, background: meta.color, fontWeight: T.fwBold, fontSize: T.fsXxxs }}>{meta.icon}</span>
            <a href={source.rawUrl ?? source.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${displayName}`} title={displayName} style={{ color: T.dark, fontWeight: T.fwSemibold, textDecoration: "none", maxWidth: compact ? 120 : 190, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {displayName}
            </a>
            {showScope && source.scope === "portfolio" && <span style={{ color: T.mid, fontSize: T.fsXxxs }}>Portfolio</span>}
            {source.status && String(source.status).toLowerCase() !== "linked" && (
              <span style={{ color: source.status === "Reading" ? T.green : T.amberDark, background: source.status === "Reading" ? T.greenSoft : T.utilAmber, borderRadius: 4, padding: "1px 4px", fontSize: T.fsXxxs, fontWeight: T.fwBold }}>
                {source.status}
              </span>
            )}
            {removable && (
              <button type="button" data-testid={`project-source-remove-${source.id}`} aria-label={`Remove ${displayName}`} title={`Remove ${displayName}`} onClick={() => onRemove(source)} style={{ border: 0, background: "transparent", color: T.mid, cursor: "pointer", padding: 0 }}>×</button>
            )}
          </span>
        );
      })}
    </div>
  );
}

export function ProjectSourcePaste({ canEdit, onAdd, label = "Paste a source link", testId = "project-source-paste" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [friendlyLabel, setFriendlyLabel] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const detected = useMemo(() => detectSourceLink(url), [url]);

  if (!canEdit) return null;

  const cancel = () => {
    setIsOpen(false);
    setUrl("");
    setFriendlyLabel("");
    setError(null);
  };

  const save = async () => {
    if (!detected) {
      setError(new Error("Enter a valid http or https URL."));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onAdd({
        url: detected.rawUrl,
        ...(friendlyLabel.trim() ? { label: friendlyLabel.trim() } : {}),
      });
      setUrl("");
      setFriendlyLabel("");
      setIsOpen(false);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={testId} style={{ marginTop: 9 }}>
      {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}
      {!isOpen ? (
        <button
          type="button"
          data-testid={`${testId}-add-button`}
          onClick={() => { setIsOpen(true); setError(null); }}
          style={addLinkStyle}
        >
          + Add link
        </button>
      ) : (
        <>
          <div style={{ display: "flex", gap: 6 }}>
            <input aria-label={label} value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://…" style={{ ...inputStyle, flex: 1 }} />
            <button type="button" onClick={save} disabled={saving} style={buttonStyle}>{saving ? "Saving…" : "Add link"}</button>
          </div>
          <input
            data-testid={`${testId}-label`}
            aria-label="Friendly source name (optional)"
            value={friendlyLabel}
            onChange={(event) => setFriendlyLabel(event.target.value)}
            placeholder="Friendly name (optional)"
            maxLength={160}
            style={{ ...inputStyle, marginTop: 6 }}
          />
          {detected && (
            <div data-testid={`${testId}-detected`} style={{ color: T.mid, fontSize: T.fsXxs, marginTop: 5 }}>
              Detected <strong>{TYPE_META[detected.sourceType]?.label || "Other link"}</strong> · {detected.displayName}
            </div>
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 7 }}>
            <button type="button" onClick={cancel} disabled={saving} style={cancelButtonStyle}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

const inputStyle = { border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 8px", fontSize: T.fsXs, color: T.dark, background: T.white };
const buttonStyle = { border: `1px solid ${T.blue}`, borderRadius: 6, padding: "6px 9px", background: T.blue, color: T.white, fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: "pointer" };
const cancelButtonStyle = { border: `1px solid ${T.border}`, borderRadius: 6, padding: "6px 9px", background: T.white, color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: "pointer" };
const addLinkStyle = { border: `1px dashed ${T.border}`, borderRadius: 8, padding: "5px 10px", background: T.tint, color: T.blue, fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: "pointer" };